// U9a — Scholars UI scoping + role-gating logic (pure functions).
// Program type is URL/route-driven (NFR Q1=C): views derive the active program type from the
// route/program and build query args from it. Role-gating predicates centralize which Scholars
// surfaces/actions a user may see.

import { AUTH_ROLES, PROGRAMS } from '../constants';

/**
 * Derive the active program type ("honors" | "scholars") from route params.
 * Falls back to Honors when no scholars indicator is present.
 * @param {object} params - react-router params (may include programType and/or programId)
 * @param {object} [programsById] - optional map of programId -> { type }
 * @returns {string} PROGRAMS.*.KEY
 */
export function deriveProgramType(params = {}, programsById = {}) {
  const { programType, programId } = params || {};
  if (programType === PROGRAMS.SCHOLARS.KEY || programType === PROGRAMS.HONORS.KEY) {
    return programType;
  }
  if (programId && programsById[programId]?.type) {
    return programsById[programId].type;
  }
  return PROGRAMS.HONORS.KEY;
}

/** True when the derived/active program type is Scholars. */
export function isScholarsScope(params = {}, programsById = {}) {
  return deriveProgramType(params, programsById) === PROGRAMS.SCHOLARS.KEY;
}

/**
 * Scholars scholars program ids follow the "SP-<name>" convention (e.g. SP-BUSINESS). Used to recognize
 * a Scholars context from a route segment such as the applicant-details programId
 * (/applicant/<id>/<programId>/<term>) where the path itself is not under /scholars.
 */
export function isScholarsSubProgramId(programId) {
  return typeof programId === 'string' && programId.startsWith('SP-');
}

/**
 * Derive the active program type from a full URL pathname for the program-type switcher.
 * Anything under /scholars is Scholars; an applicant route whose programId is "scholars" or a
 * Scholars scholars program (SP-*) is also Scholars; everything else is Honors.
 */
export function deriveProgramTypeFromPathname(pathname = '') {
  if (pathname.startsWith('/scholars')) { return PROGRAMS.SCHOLARS.KEY; }
  const [, first, , programId] = pathname.split('/');
  if (first === 'applicant' && (programId === PROGRAMS.SCHOLARS.KEY || isScholarsSubProgramId(programId))) { return PROGRAMS.SCHOLARS.KEY; }
  return PROGRAMS.HONORS.KEY;
}

export function getProgramIdFromPath() { return deriveProgramTypeFromPathname(window.location.pathname); }

/**
 * Build GraphQL query variables scoped to the active program type.
 * Never merges Honors + Scholars (BR-P1): callers pass the resulting `program` through.
 */
export function buildScholarsQueryArgs(params = {}, extra = {}, programsById = {}) {
  return { program: deriveProgramType(params, programsById), ...extra };
}

// --- Role-gating predicates (BR-A1..A5) --------------------------------------

const has = (roles, role) => Array.isArray(roles) && roles.includes(role);

// Scholars authority is derived from SCHOLARS-* roles ONLY. The generic ADMIN-ACCESS role is a
// Honors-side role (a Honors admin holds HONORS-ADMIN + ADMIN-ACCESS) and must NOT confer any Scholars
// capability. This keeps a Scholars Program Manager who is also a Honors admin scoped to the PM
// surfaces on the Scholars side rather than being elevated to a Scholars admin.

/** Any Scholars staff role → may see the Scholars switcher option. */
export function canSeeScholarsScope(roles = []) {
  return (
    has(roles, AUTH_ROLES.SCHOLARS_ADMIN) ||
    has(roles, AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER) ||
    has(roles, AUTH_ROLES.SCHOLARS_REVIEWER)
  );
}

/** Scholars admin-only surfaces (config, manage reviewers, release, publishing). */
export function canAdministerScholars(roles = []) {
  return has(roles, AUTH_ROLES.SCHOLARS_ADMIN);
}

/** Pull-next + admit-from-alternate (PM or Scholars admin). */
export function canManageScholarsQueue(roles = []) {
  return (
    has(roles, AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER) ||
    has(roles, AUTH_ROLES.SCHOLARS_ADMIN)
  );
}

/**
 * Record a Scholars Admit/Deny decision (submitScholarsRecommendation). Reviewers work alongside
 * Program Managers and may decide on applicants in programs they're granted on, so this is broader
 * than canManageScholarsQueue (which still gates PM/admin-only queue operations like pull-next and
 * admit-from-alternate). The backend is the authority: submitScholarsRecommendation is authorized for
 * SCHOLARS-REVIEWER, and ProgramQueueService.assertQueueAccess allows an active reviewer grant on the
 * program — this UI predicate only decides whether to render the decision controls.
 */
export function canDecideScholars(roles = []) {
  return (
    has(roles, AUTH_ROLES.SCHOLARS_ADMIN) ||
    has(roles, AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER) ||
    has(roles, AUTH_ROLES.SCHOLARS_REVIEWER)
  );
}

/** Reviewer role → subject to the admin-only field data boundary (F3). */
export function isScholarsReviewerOnly(roles = []) {
  return (
    has(roles, AUTH_ROLES.SCHOLARS_REVIEWER) &&
    !has(roles, AUTH_ROLES.SCHOLARS_ADMIN) &&
    !has(roles, AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER)
  );
}

// --- has543: Honors/Scholars program-side access + nav-toggle visibility ----
//
// Rules (has543v2 — loosened from dual-ADMIN to ANY dual role):
//   - Toggle shows for ANY user holding at least one Honors-side role AND at least one Scholars-side
//     role (e.g. HONORS-REVIEWER + SCHOLARS-PROGRAM-MANAGER, or dual-admins). Previously it required
//     admin on BOTH sides; now any cross-program user gets the switcher.
//   - Single-side users (roles on only one program) see no toggle and only their own side.
//   - When the toggle shows, default landing is Honors unless a browser-persisted preference says
//     otherwise.

/** Any Honors-side role (admin or not). ADMIN-ACCESS counts as Honors (generic admin). */
export function hasHonorsAccess(roles = []) {
  return (
    has(roles, AUTH_ROLES.ADMIN_ACCESS) ||
    has(roles, AUTH_ROLES.HONORS_ADMIN) ||
    has(roles, AUTH_ROLES.HONORS_SEASONAL_MANAGER) ||
    has(roles, AUTH_ROLES.HONORS_PROGRAM_COORDINATOR) ||
    has(roles, AUTH_ROLES.HONORS_REVIEWER) ||
    has(roles, AUTH_ROLES.READ_ACCESS)
  );
}

/** Any Scholars-side role. */
export function hasScholarsAccess(roles = []) {
  return (
    has(roles, AUTH_ROLES.SCHOLARS_ADMIN) ||
    has(roles, AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER) ||
    has(roles, AUTH_ROLES.SCHOLARS_REVIEWER)
  );
}

/** Honors admin: HONORS-ADMIN or the generic ADMIN-ACCESS. */
export function isHonorsAdminRole(roles = []) {
  return has(roles, AUTH_ROLES.HONORS_ADMIN) || has(roles, AUTH_ROLES.ADMIN_ACCESS);
}

/** Scholars admin. */
export function isScholarsAdminRole(roles = []) {
  return has(roles, AUTH_ROLES.SCHOLARS_ADMIN);
}

/**
 * Dual-admin check: admin on BOTH sides — (HONORS-ADMIN || ADMIN-ACCESS) AND SCHOLARS-ADMIN.
 * Retained for callers that specifically need the admin-on-both-sides condition. NOTE: this is no
 * longer the toggle gate — see hasDualProgramAccess.
 */
export function hasBothAdminRoles(roles = []) {
  return isHonorsAdminRole(roles) && isScholarsAdminRole(roles);
}

/**
 * Toggle visibility (has543v2): show when the user has ANY Honors-side role AND ANY Scholars-side
 * role. This is the loosened rule — the switcher (and the default-landing preference control) appear
 * for any cross-program user, not just dual-admins. Single-side users get no toggle.
 */
export function hasDualProgramAccess(roles = []) {
  return hasHonorsAccess(roles) && hasScholarsAccess(roles);
}

/**
 * Resolve the program side a user should land on.
 *   - Dual-admin: use the persisted preference when it's a valid program key, else default to Honors.
 *   - Single-side user: always their own side (preference is irrelevant / ignored).
 *   - No recognized role: default Honors (defensive; routing/auth handles unauthorized separately).
 * @param {string[]} roles
 * @param {string|null|undefined} storedPreference - PROGRAMS.HONORS.KEY | PROGRAMS.SCHOLARS.KEY | null
 * @returns {string} PROGRAMS.HONORS.KEY | PROGRAMS.SCHOLARS.KEY
 */
export function resolveDefaultProgramSide(roles = [], storedPreference = null) {
  if (hasDualProgramAccess(roles)) {
    if (storedPreference === PROGRAMS.SCHOLARS.KEY || storedPreference === PROGRAMS.HONORS.KEY) {
      return storedPreference;
    }
    return PROGRAMS.HONORS.KEY; // default landing for dual-program users
  }
  if (hasScholarsAccess(roles) && !hasHonorsAccess(roles)) { return PROGRAMS.SCHOLARS.KEY; }
  return PROGRAMS.HONORS.KEY;
}
